import { useEffect, useMemo, useState } from 'react'
import type { PmmRepository } from '../data/repository'
import { laneMeta } from '../domain/assets'
import type { AssetFeedback, AssetLane, Capacity, DailyAsset, DailyPlan } from '../domain/dailyEngine'
import { CAPACITY_MODES } from '../domain/capacityMode'
import { ResponseField } from './ResponseField'
import { GuidedAssetContent } from './GuidedAssetContent'
import '../daily.css'
import '../loop07-daily.css'

interface UserWorkspaceProps { repository: PmmRepository; capacity: Capacity }

const laneOrder: AssetLane[] = ['self', 'relationship', 'fatherhood']
const feedbackLabels: Record<AssetFeedback, string> = { helpful: 'Helpful', neutral: 'Neutral', poor_fit: 'Poor fit', too_much: 'Too much', not_now: 'Not now', unsafe: 'Unsafe', already_doing: 'Already doing' }
const localDate = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())

function mutationId(kind: string, assetId: string): string {
  return `${kind}-${assetId}-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`
}

export function TodayWorkspace({ repository, capacity }: UserWorkspaceProps) {
  const activeMode = CAPACITY_MODES[capacity]
  const [plan, setPlan] = useState<DailyPlan | null>(null)
  const [activeLane, setActiveLane] = useState<AssetLane>('relationship')
  const [activeAssetId, setActiveAssetId] = useState('')
  const [responses, setResponses] = useState<Record<string, string>>({})
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({})
  const [shareSelectedOptions, setShareSelectedOptions] = useState<Record<string, boolean>>({})
  const [feedback, setFeedback] = useState<Record<string, AssetFeedback>>({})
  const [notice, setNotice] = useState('')
  const [shelfOpen, setShelfOpen] = useState(true)
  const [snoozeUntil, setSnoozeUntil] = useState('')
  const [busy, setBusy] = useState('loading')

  useEffect(() => {
    let active = true
    setBusy('loading')
    setShelfOpen(capacity !== 'low')
    repository.getDailyExperience({ localDate: localDate(), capacity }).then((next) => {
      if (!active) return
      setPlan(next)
      const preferred = next.assets.find((asset) => next.focusAssetIds.includes(asset.assetId) && asset.lane === activeLane)
        ?? next.assets.find((asset) => next.focusAssetIds.includes(asset.assetId))
        ?? next.assets[0]
      if (preferred) { setActiveLane(preferred.lane); setActiveAssetId(preferred.assetId) }
      setBusy('')
    }).catch((error) => {
      if (!active) return
      setNotice(error instanceof Error ? error.message : 'Today could not be prepared safely.')
      setBusy('')
    })
    return () => { active = false }
  }, [repository, capacity])

  useEffect(() => {
    let active = true
    const sync = () => {
      void repository.syncOfflineQueue().then((report) => {
        if (!active || (!report.synced && !report.pending && !report.conflicts)) return
        setNotice(report.conflicts
          ? `${report.synced} offline action${report.synced === 1 ? '' : 's'} synced; ${report.conflicts} need a fresh review.`
          : `${report.synced} offline action${report.synced === 1 ? '' : 's'} synced safely. ${report.pending ? `${report.pending} still queued.` : ''}`)
      }).catch(() => undefined)
    }
    window.addEventListener('online', sync)
    if (navigator.onLine) sync()
    return () => { active = false; window.removeEventListener('online', sync) }
  }, [repository])

  const laneAssets = useMemo(() => plan?.assets.filter((asset) => asset.lane === activeLane) ?? [], [plan, activeLane])
  const activeAsset = plan?.assets.find((asset) => asset.assetId === activeAssetId)
    ?? laneAssets[0]
    ?? plan?.assets[0]
  const focusAssets = plan?.assets.filter((asset) => plan.focusAssetIds.includes(asset.assetId)) ?? []
  const shelfAssets = plan?.assets.filter((asset) => plan.shelfAssetIds.includes(asset.assetId)) ?? []

  function openAsset(asset: DailyAsset) {
    setActiveLane(asset.lane)
    setActiveAssetId(asset.assetId)
  }

  async function record(action: 'draft' | 'completed' | 'skipped' | 'snoozed', asset = activeAsset, feedbackOverride?: AssetFeedback) {
    if (!plan || !asset) return
    setBusy(action)
    try {
      const result = await repository.recordDailyInteraction({
        assetId: asset.assetId, planId: plan.planId, localDate: plan.localDate, action,
        value: responses[asset.assetId] ?? '', feedback: feedbackOverride ?? feedback[asset.assetId],
        selectedOption: selectedOptions[asset.assetId] ?? '',
        allowSelectedOptionForPersonalisation: shareSelectedOptions[asset.assetId] === true,
        ...(action === 'snoozed' && snoozeUntil ? { snoozeUntil: new Date(snoozeUntil).toISOString() } : {}),
        clientMutationId: mutationId(action, asset.assetId),
      })
      if (result.queued) {
        setNotice(`Saved to the encrypted device queue. ${result.pendingCount ?? 1} action${result.pendingCount === 1 ? '' : 's'} will sync with the same receipt when you reconnect.`)
        return
      }
      if (feedbackOverride === 'unsafe') {
        const next = await repository.regenerateDailyAsset({ localDate: plan.localDate, capacity, assetId: asset.assetId, expectedRevision: plan.revision, clientMutationId: mutationId('unsafe-swap', asset.assetId) })
        setPlan(next)
        const replacementId = [...next.focusAssetIds, ...next.shelfAssetIds].find((id) => ![...plan.focusAssetIds, ...plan.shelfAssetIds].includes(id))
        const replacement = next.assets.find((item) => item.assetId === replacementId)
        if (replacement) openAsset(replacement)
        setNotice('Practice paused for safety review and removed from Today. A same-lane alternative replaced it without AI.')
        return
      }
      setNotice(action === 'completed' ? 'Action recorded. Only your explicit feedback can say whether it helped.' : action === 'draft' ? 'Private reflection saved outside AI context.' : action === 'snoozed' ? 'Snoozed to the time you chose. No failure was recorded.' : `Practice ${action}. No judgment and no false completion claim.`)
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'That update could not be saved safely.')
    } finally { setBusy('') }
  }

  async function regenerate(asset = activeAsset) {
    if (!plan || !asset) return
    setBusy('swap')
    try {
      const next = await repository.regenerateDailyAsset({
        localDate: plan.localDate, capacity, assetId: asset.assetId, expectedRevision: plan.revision,
        clientMutationId: mutationId('swap', asset.assetId),
      })
      setPlan(next)
      const replacementId = [...next.focusAssetIds, ...next.shelfAssetIds].find((id) => ![...plan.focusAssetIds, ...plan.shelfAssetIds].includes(id))
      const replacement = next.assets.find((item) => item.assetId === replacementId)
      if (replacement) openAsset(replacement)
      setNotice('Replaced deterministically from your eligible library. No AI call was made.')
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'No safe alternative is available right now.')
    } finally { setBusy('') }
  }

  return (
    <main className="workspace user-workspace">
      <section className="user-hero">
        <div>
          <p className="eyebrow">Your Primal Path · today</p>
          <h1>Lead yourself.<br /><em>Love in action.</em></h1>
          <p>Choose one useful action from your Coach-reviewed path. The optional shelf is there when you have more space—not as a checklist you have to finish.</p>
        </div>
        <div className="today-pace-badge"><span>Today’s mode</span><strong>{activeMode.name}</strong><small>One honest choice, shared across both journeys.</small></div>
      </section>


      {busy === 'loading' && <section className="locked-review"><span>Preparing today</span><h3>Applying consent, safety, cooldown and diversity rules…</h3></section>}

      {plan?.deliveryState === 'offline_cache' && <section className="offline-today-banner" role="status"><strong>Offline Today</strong><span>This is your encrypted cached plan. New actions queue with exact receipts; regeneration waits for a connection.</span></section>}
      {plan?.availability === 'waiting_for_coach' && (
        <section className="personal-coaching-waiting" aria-labelledby="personal-path-waiting-title">
          <div className="waiting-mark" aria-hidden="true"><span /><span /><span /></div>
          <p className="eyebrow">Coach-guided. Never generic.</p>
          <h2 id="personal-path-waiting-title">Your personal coaching path is being prepared.</h2>
          <p>Nothing generic has been placed here. Your Coach will use only the onboarding information and later answers you choose to share, review every recommendation, and publish your first personal practices when they are ready.</p>
          <div className="waiting-boundaries">
            <span><strong>Personal to you</strong>No fixed fallback library</span>
            <span><strong>Coach reviewed</strong>Nothing publishes itself</span>
            <span><strong>Private by design</strong>Your private writing stays excluded</span>
          </div>
        </section>
      )}
      {plan && plan.availability !== 'waiting_for_coach' && (
        <>
          <section className="focus-section" aria-labelledby="focus-title">
            <div className="section-heading"><div><p className="eyebrow">Your focus</p><h2 id="focus-title">A small set worth completing</h2></div><p>{focusAssets.length} selected · revision {plan.revision}</p></div>
            <div className="focus-grid">
              {focusAssets.map((asset) => <button type="button" key={asset.assetId} className={`focus-card ${activeAsset?.assetId === asset.assetId ? 'is-active' : ''}`} onClick={() => openAsset(asset)}><span>{laneMeta[asset.lane].label}</span><strong>{asset.title}</strong><small>{asset.durationMinutes} min · {asset.categoryLabel}</small></button>)}
            </div>
          </section>

          <section className="practice-shell">
            <nav className="lane-tabs" aria-label="Practice lanes">
              {laneOrder.map((lane) => {
                const count = plan.assets.filter((asset) => asset.lane === lane).length
                if (!count) return null
                return <button type="button" key={lane} className={activeLane === lane ? 'is-active' : ''} onClick={() => { setActiveLane(lane); const first = plan.assets.find((asset) => asset.lane === lane); if (first) setActiveAssetId(first.assetId) }}><strong>{laneMeta[lane].label}</strong><span>{count} practices</span></button>
              })}
            </nav>

            <div className="lane-explanation"><p className="eyebrow">{laneMeta[activeLane].label}</p><h2>{laneMeta[activeLane].description}</h2></div>

            <div className="category-tabs" role="tablist" aria-label={`${activeLane} practices`}>
              {laneAssets.map((asset) => <button type="button" role="tab" aria-selected={activeAsset?.assetId === asset.assetId} key={asset.assetId} className={activeAsset?.assetId === asset.assetId ? 'is-active' : ''} onClick={() => setActiveAssetId(asset.assetId)}><span>{plan.focusAssetIds.includes(asset.assetId) ? 'Focus' : 'Optional'}</span>{asset.title}</button>)}
            </div>

            {activeAsset && <article className="daily-practice">
              <header><div><p className="eyebrow">{activeAsset.categoryLabel}</p><h2>{activeAsset.title}</h2><p>{activeAsset.purpose}</p></div><div className="practice-time"><strong>{activeAsset.durationMinutes}</strong><span>minutes</span></div></header>
              <div className="why-today"><strong>Why today</strong><p>{activeAsset.whyToday}</p><div className="reason-codes">{activeAsset.reasonCodes.map((code) => <span key={code}>{code.replaceAll('_', ' ')}</span>)}</div></div>
              {activeAsset.consentCue && <div className="consent-cue"><strong>Choice and consent</strong><p>{activeAsset.consentCue}</p></div>}
              <ol className="practice-steps">{activeAsset.instructions.map((instruction, index) => <li key={instruction}><span>{String(index + 1).padStart(2, '0')}</span><p>{instruction}</p></li>)}</ol>
              <GuidedAssetContent asset={activeAsset} />
              <ResponseField
                kind={activeAsset.responseKind}
                label={activeAsset.responsePrompt}
                value={responses[activeAsset.assetId] ?? ''}
                onChange={(value) => setResponses((current) => ({ ...current, [activeAsset.assetId]: value }))}
                options={activeAsset.responseOptions}
                selectedOption={selectedOptions[activeAsset.assetId] ?? ''}
                onOptionChange={(value) => setSelectedOptions((current) => ({ ...current, [activeAsset.assetId]: value }))}
                responseOptional={activeAsset.responseOptional}
                freeTextOptional={activeAsset.freeTextOptional}
              />
              {activeAsset.responseOptions?.length && selectedOptions[activeAsset.assetId] ? <label className="personalisation-choice">
                <input type="checkbox" checked={shareSelectedOptions[activeAsset.assetId] === true} onChange={(event) => setShareSelectedOptions((current) => ({ ...current, [activeAsset.assetId]: event.target.checked }))} />
                <span><strong>Allow this answer to inform me through personalisation and better understanding of the man you are. This will be kept private.</strong>Your optional written note stays private and is never sent to AI. Privacy choices stay private even if this is checked.</span>
              </label> : null}
              <fieldset className="fit-feedback"><legend>How does this fit you today?</legend>{(['helpful', 'neutral', 'poor_fit', 'too_much', 'not_now', 'already_doing'] as AssetFeedback[]).map((value) => <button type="button" key={value} className={feedback[activeAsset.assetId] === value ? 'is-active' : ''} onClick={() => setFeedback((current) => ({ ...current, [activeAsset.assetId]: value }))}>{feedbackLabels[value]}</button>)}</fieldset>
              <label className="snooze-control"><span>Snooze until a time you choose</span><input type="datetime-local" value={snoozeUntil} onChange={(event) => setSnoozeUntil(event.target.value)} /></label>
              <footer className="practice-actions"><button type="button" className="quiet-action" disabled={Boolean(busy) || plan.deliveryState === 'offline_cache'} onClick={() => void regenerate()}>Regenerate</button><button type="button" className="quiet-action" disabled={Boolean(busy) || !snoozeUntil} onClick={() => void record('snoozed')}>Snooze</button><button type="button" className="quiet-action" disabled={Boolean(busy)} onClick={() => void record('skipped')}>Skip</button><button type="button" className="unsafe-action" disabled={Boolean(busy)} onClick={() => void record('skipped', activeAsset, 'unsafe')}>This feels unsafe</button><button type="button" className="quiet-action" disabled={Boolean(busy)} onClick={() => void record('draft')}>Save private reflection</button><button type="button" className="approve-action" disabled={Boolean(busy)} onClick={() => void record(feedback[activeAsset.assetId] === 'not_now' ? 'skipped' : 'completed')}>{feedback[activeAsset.assetId] === 'not_now' ? 'Record not now' : 'Record action'}</button></footer>
              <p className="completion-signal"><strong>Action occurred when:</strong> {activeAsset.completionSignal} Completion alone does not prove benefit or anyone else's response.</p>
            </article>}
          </section>

          <section className={`optional-shelf ${shelfOpen ? 'is-open' : 'is-collapsed'}`}><div className="section-heading"><div><p className="eyebrow">Optional shelf</p><h2>More ways to invest today</h2></div><button type="button" className="shelf-toggle" aria-expanded={shelfOpen} onClick={() => setShelfOpen((open) => !open)}>{shelfOpen ? 'Collapse optional shelf' : `Show ${shelfAssets.length} optional practices`}</button></div>{shelfOpen && <div className="shelf-grid">{shelfAssets.map((asset) => <article key={asset.assetId}><span>{laneMeta[asset.lane].label}</span><h3>{asset.title}</h3><p>{asset.purpose}</p><footer><small>{asset.durationMinutes} min</small><button type="button" onClick={() => openAsset(asset)}>Open</button><button type="button" disabled={plan.deliveryState === 'offline_cache'} onClick={() => void regenerate(asset)}>Regenerate</button></footer></article>)}</div>}</section>
        </>
      )}

      {notice && <div className="toast" role="status"><span />{notice}<button type="button" onClick={() => setNotice('')} aria-label="Dismiss message">×</button></div>}
    </main>
  )
}
